import { initializeApp, type FirebaseApp } from 'firebase/app';
import { getMessaging, getToken, onMessage, type MessagePayload } from 'firebase/messaging';
import axios from 'axios';
import { getNotificationNavigationPath } from 'utils/notificationNavigation';

const LOG_PREFIX = '[WebPush]';

const getEnv = (key: string) => {
  const value = process.env[key];
  return value && value.trim().length ? value.trim() : undefined;
};

const buildFirebaseConfig = () => {
  const apiKey = getEnv('REACT_APP_FIREBASE_API_KEY');
  const authDomain = getEnv('REACT_APP_FIREBASE_AUTH_DOMAIN');
  const projectId = getEnv('REACT_APP_FIREBASE_PROJECT_ID');
  const storageBucket = getEnv('REACT_APP_FIREBASE_STORAGE_BUCKET');
  const messagingSenderId = getEnv('REACT_APP_FIREBASE_MESSAGING_SENDER_ID');
  const appId = getEnv('REACT_APP_FIREBASE_APP_ID');

  const missing = [
    !apiKey && 'REACT_APP_FIREBASE_API_KEY',
    !authDomain && 'REACT_APP_FIREBASE_AUTH_DOMAIN',
    !projectId && 'REACT_APP_FIREBASE_PROJECT_ID',
    !storageBucket && 'REACT_APP_FIREBASE_STORAGE_BUCKET',
    !messagingSenderId && 'REACT_APP_FIREBASE_MESSAGING_SENDER_ID',
    !appId && 'REACT_APP_FIREBASE_APP_ID'
  ].filter(Boolean);

  if (missing.length) {
    console.warn(`${LOG_PREFIX} Missing env: ${missing.join(', ')}`);
    return null;
  }

  return {
    apiKey: apiKey!,
    authDomain: authDomain!,
    projectId: projectId!,
    storageBucket: storageBucket!,
    messagingSenderId: messagingSenderId!,
    appId: appId!
  };
};

let firebaseApp: FirebaseApp | null = null;
let messaging: ReturnType<typeof getMessaging> | null = null;
let initError: unknown = null;

try {
  const firebaseConfig = buildFirebaseConfig();
  if (firebaseConfig) {
    firebaseApp = initializeApp(firebaseConfig);
    messaging = getMessaging(firebaseApp);
    // console.log(`${LOG_PREFIX} Firebase initialized projectId=${firebaseConfig.projectId}`);
  } else {
    initError = new Error('Incomplete Firebase env configuration');
    console.error(`${LOG_PREFIX} Firebase not initialized — check .env`);
  }
} catch (e) {
  initError = e;
  messaging = null;
  console.error(`${LOG_PREFIX} Firebase init failed:`, e);
}

const registerMessagingServiceWorker = async (): Promise<ServiceWorkerRegistration | null> => {
  if (!('serviceWorker' in navigator)) {
    console.warn(`${LOG_PREFIX} Service workers not supported in this browser`);
    return null;
  }
  try {
    const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
    console.log(`${LOG_PREFIX} Service worker registered scope=${registration.scope}`);
    return registration;
  } catch (err) {
    console.error(`${LOG_PREFIX} Service worker registration failed:`, err);
    return null;
  }
};

export const requestForToken = async () => {
  if (!('Notification' in window)) {
    console.log(`${LOG_PREFIX} This browser does not support notifications`);
    return null;
  }

  if (!messaging) {
    console.error(`${LOG_PREFIX} Firebase not initialized:`, initError);
    return null;
  }

  const permission = await Notification.requestPermission();
  // console.log(`${LOG_PREFIX} Notification permission=${permission}`);
  if (permission !== 'granted') {
    console.log(`${LOG_PREFIX} Permission not granted — web push disabled`);
    return null;
  }

  const vapidKey = getEnv('REACT_APP_FIREBASE_VAPID_KEY');
  if (!vapidKey) {
    console.warn(`${LOG_PREFIX} Missing REACT_APP_FIREBASE_VAPID_KEY`);
    return null;
  }

  const swRegistration = await registerMessagingServiceWorker();
  if (!swRegistration) {
    console.warn(`${LOG_PREFIX} No service worker — getToken may fail`);
  }

  try {
    const currentToken = await getToken(messaging, {
      vapidKey,
      serviceWorkerRegistration: swRegistration ?? undefined
    });

    if (currentToken) {
      console.log(`${LOG_PREFIX} FCM token prefix=${currentToken.slice(0, 12)}...`);
      await sendTokenToBackend(currentToken);
      console.log(`${LOG_PREFIX} SUMMARY permission=granted token=yes backend=attempted`);
      return currentToken;
    }

    console.log(
      `${LOG_PREFIX} No registration token — check VAPID key and firebase-messaging-sw.js project`
    );
    return null;
  } catch (err) {
    console.error(`${LOG_PREFIX} Error retrieving token:`, err);
    return null;
  }
};

const sendTokenToBackend = async (token: string) => {
  try {
    await axios.post('/users/device-token', { token, deviceType: 'web' });
    console.log(`${LOG_PREFIX} Device token registered with API`);
  } catch (error) {
    console.error(`${LOG_PREFIX} Error sending token to backend:`, error);
  }
};

export const onMessageListener = (callback: (payload: MessagePayload) => void) => {
  if (!messaging) {
    console.error(`${LOG_PREFIX} Firebase not initialized:`, initError);
    return () => {};
  }
  return onMessage(messaging, (payload) => {
    // console.log(`${LOG_PREFIX} Foreground message`, payload);
    window.dispatchEvent(new CustomEvent('notifications:refresh'));
    const notificationId = payload.data?.notificationId;
    if (Notification.permission === 'granted' && payload.notification) {
      const browserNotification = new Notification(payload.notification.title || 'Notification', {
        body: payload.notification.body,
        data: payload.data
      });
      browserNotification.onclick = () => {
        const data = payload.data || {};
        const targetId = notificationId || data.id || data.notificationId;
        const path = getNotificationNavigationPath({
          id: Number(targetId) || 0,
          type: data.type || '',
          data
        });
        window.location.href = path;
      };
    }
    callback(payload);
  });
};
